/**
 * CafeteriaWorkflowHeader — the slim top bar for the focused, shell-less
 * cafeteria task surfaces (Serve meals, Confirm deliveries, Complete
 * service). Title on the left; caller-supplied actions (Save for later,
 * Complete service, etc.) on the right. Mirrors the WorkflowHeader
 * pattern used by the temperature-log / onboarding focused pages.
 *
 * PR6: the offline pending-charges pill mounts here so queued POS
 * sales stay visible on every cafeteria page (design pass 00 §5).
 * It renders nothing when the queue holds no `posSale` rows.
 *
 * PROD-1302: the row is a 1fr/auto/1fr grid — title left, campus CENTERED,
 * actions right. The campus sits outside the `leading ?? (…)` fallback because
 * `leading` REPLACES the title block and the Serve page passes one, so a campus
 * rendered inside it would silently never appear on the busiest cafeteria
 * screen. The equal side tracks are what centre it against the header rather
 * than against whatever the title happens to be.
 *
 * Oct 19, 2026: the markup moved to `@ordo/ui/workflow-header` (Menus' Packaging
 * data wizard reuses this chrome). This wrapper keeps its API and passes only
 * the cafeteria bits into the shared header's slots: the campus into `center`,
 * the pending-charges pill ahead of the caller's `actions`. Same DOM, so Ordo
 * renders pixel-identical.
 */

import { WorkflowHeader } from '@ordo/ui/workflow-header'
import type { ReactNode } from 'react'

import { CampusBar } from './campus-bar'
import { PendingChargesPill } from './pending-charges-pill'

export interface CafeteriaWorkflowHeaderProps {
  title?: string
  subtitle?: string
  /**
   * Replaces the title block entirely (POS-style chrome). The Serve page
   * passes its X close button here; the other wizard pages keep their
   * `title` untouched.
   */
  leading?: ReactNode
  actions?: ReactNode
  /**
   * PROD-1302 — the campus to name in the centre of the row. Pass `null` from
   * the production-record dialog, which hosts these flows outside cafeteria
   * mode and already names the campus around them.
   */
  locationId?: string | null
}

export function CafeteriaWorkflowHeader({
  title,
  subtitle,
  leading,
  actions,
  locationId = null,
}: CafeteriaWorkflowHeaderProps) {
  return (
    <WorkflowHeader
      title={title}
      subtitle={subtitle}
      leading={leading}
      center={locationId ? <CampusBar locationId={locationId} /> : null}
      actions={
        <>
          <PendingChargesPill />
          {actions}
        </>
      }
    />
  )
}
